import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TournamentEntity } from 'app/interfaces/entity';
import { TournamentTile } from 'modules/tournaments/components/TournamentTile';
import { useTournamentModal } from 'modules/tournaments/hooks';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useStyles } from 'shared/styles/HomePageCarousel.styles';
import { TournamentTileDateStateProvider } from 'modules/tournaments/providers';
import { CreateGameActivity } from 'modules/games/components/CreateGameActivity';
import Swiper from 'swiper';
import { EmptyTournamentListPlaceholder } from '../EmptyTournamentListPlaceholder';
import { register } from 'swiper/swiper-element';

register();

interface TournamentsCarouselProps {
  tournaments: TournamentEntity[];
}

export const TournamentsCarousel: FC<TournamentsCarouselProps> = ({ tournaments }) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const swiperRef = useRef<Swiper | null>(null);

  const isDesktopView = useMediaQuery(theme.breakpoints.up('lg'));

  const { setTournamentIdQueryParam } = useTournamentModal();

  const isTournamentsPresent = useMemo(() => Boolean(tournaments?.length), [tournaments?.length]);

  const onNextHandler = useCallback(() => swiperRef.current?.swiper?.slideNext(), []);
  const onPrevHandler = useCallback(() => swiperRef.current?.swiper?.slidePrev(), []);

  useEffect(() => {
    if (!swiperRef?.current) {
      return;
    }

    const params = { loop: true, slidesPerView: 'auto' };
    Object.assign(swiperRef.current, params);
    swiperRef.current.initialize();
  }, [isDesktopView]);

  return (
    <Box className={classes.root}>
      <TournamentTileDateStateProvider isDateHidden>
        <Box className={classes.header}>
          <Typography variant="h3">Featured Tournaments</Typography>
          {isDesktopView && (
            <Box className={classes.headerButtons}>
              <CreateGameActivity isMatch={false} isTournament={true} />
              {isTournamentsPresent && (
                <Box className={classes.arrows}>
                  <ChevronLeftIcon className={classes.arrow} onClick={onPrevHandler} />
                  <ChevronRightIcon onClick={onNextHandler} className={classes.arrow} />
                </Box>
              )}
            </Box>
          )}
        </Box>
        {isTournamentsPresent ? (
          <Box className={classes.divider}>
            <swiper-container init={false} ref={swiperRef}>
              {tournaments.map((tournament) => (
                <swiper-slide key={tournament.id}>
                  <TournamentTile tournament={tournament} onClick={setTournamentIdQueryParam} />
                </swiper-slide>
              ))}
            </swiper-container>
          </Box>
        ) : (
          <EmptyTournamentListPlaceholder />
        )}
      </TournamentTileDateStateProvider>
    </Box>
  );
};
